import { useCommissionAmount } from '@/app/hooks/useCommissionAmount'
import { cn } from '@/app/utils/cn'
import { formatMoneyValue, moneyMask } from '@/app/utils/formatMoneyValue'
import { BasicInput } from '@/view/components/BasicInput'
import { Button } from '@/view/components/ui/button'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
} from '@/view/components/ui/dialog'
import { Spinner } from '@/view/components/ui/spinner'
import { useToast } from '@/view/components/ui/use-toast'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowRight2, Clock, Money2 } from 'iconsax-react'
import { AlertTriangle } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'

const formValidation = z.object({
  value: z
    .string()
    .min(1, '* Obrigatório')
    .refine((f) => +f.replace(/\D/g, '') > 0, 'Deve ser maior que R$0,00')
    .transform((f) => +f.replace(/\D/g, '') || 0),
})

type formData = z.infer<typeof formValidation>

interface StatementItemProps {
  label: string
  value: number
}

const StatementItem = ({ label, value }: StatementItemProps) => {
  return (
    <div className="flex items-center justify-between">
      <p className="text-black04">{label}</p>
      <p className="font-medium text-black01">{formatMoneyValue(value)}</p>
    </div>
  )
}

export default function WithdrawModal() {
  const form = useForm<formData>({
    resolver: zodResolver(formValidation),
  })
  const [open, setOpen] = useState(false)
  const { toast } = useToast()
  const {
    data,
    isLoading,
    createWithdraw,
    isAdmin,
    dataAdmin,
    isLoadingAdmin,
  } = useCommissionAmount()

  const {
    register,
    formState: { errors, isSubmitting },
    setError,
    reset,
  } = form

  const handleSubmit = form.handleSubmit(async ({ value }) => {
    try {
      if (data && value < data?.config.minWithdrawAmount) {
        setError('value', {
          type: 'custom',
          message: 'Valor do saque é menor do que o mínimo permitido.',
        })
        throw new Error('Valor do saque é menor do que o mínimo permitido.')
      }

      if (
        isAdmin &&
        (dataAdmin?.adminAmount === 0 ||
          (dataAdmin?.adminAmount && value > dataAdmin?.adminAmount))
      ) {
        setError('value', {
          type: 'custom',
          message: 'Valor do saque é menor',
        })
        throw new Error('Valor do saque é menor throw')
      }

      if (
        !isAdmin &&
        (data?.amount === 0 || (data?.amount && value > data.amount))
      ) {
        setError('value', {
          type: 'custom',
          message: 'Valor do saque diferente',
        })
        throw new Error('Valor do saque esse é throw')
      }

      await createWithdraw(value)
      toast({
        variant: 'default',
        title: 'Pix realizado com sucesso!',
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      toast({
        variant: 'destructive',
        title: e.message || 'Erro ao sacar, tente novamente mais tarde.',
      })
    }
  })

  const hasFreeWithdraw = !!(
    data?.config.withdrawsRemaining && data.config.withdrawsRemaining > 0
  )

  useEffect(() => {
    const value = isAdmin ? dataAdmin?.adminAmount : data?.amount || 0
    if (!value) return
    reset({
      value: formatMoneyValue(value > 0 ? value : 0) as unknown as number,
    })
  }, [data?.amount, reset])

  const statmentItems: StatementItemProps[] = useMemo(
    () => [
      {
        label: 'Saques',
        value: data?.extract.SELLER || 0,
      },
      {
        label: 'Bônus',
        value: data?.extract.BONUS || 0,
      },
      {
        label: 'Líder',
        value: data?.extract.LEADER || 0,
      },
      {
        label: 'Supervisor',
        value: data?.extract.SUPERVISOR || 0,
      },
      {
        label: 'Indicações',
        value: data?.extract.REFFERED || 0,
      },
      {
        label: 'Bônus equipe',
        value: data?.extract.RETROACTIVE || 0,
      },
    ],
    [data],
  )

  const amount = useMemo(() => {
    const res = data?.amount || 0

    return res > 0 ? res : 0
  }, [data?.amount])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <a className="flex cursor-pointer items-center text-primary">
          Sacar agora <ArrowRight2 size={18} />
        </a>
      </DialogTrigger>

      <DialogContent>
        <FormProvider {...form}>
          <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
            <div>
              <p className="text-lg font-medium">Escolha o valor do saque</p>
              <p className="text-black04">
                Saldo disponível para saque:{' '}
                <span className="font-medium text-black01">
                  {!isAdmin && formatMoneyValue(amount)}
                  {isAdmin && formatMoneyValue(dataAdmin?.adminAmount || 0)}
                </span>
              </p>
            </div>
            <BasicInput
              sizeInput="large"
              className="text-xl"
              placeholder="R$ 0.000.00"
              error={errors.value?.message}
              onInput={moneyMask}
              {...register('value')}
            />

            {data?.extract && (
              <div>
                <p className="text-lg font-medium">Extrato de comissões</p>
                <div className="mt-1 flex flex-col gap-1.5">
                  {statmentItems
                    ?.filter(({ value }) => value > 0)
                    .map((item) => (
                      <StatementItem key={item.label} {...item} />
                    ))}
                </div>
              </div>
            )}
            <div
              className={cn(
                'flex flex-col gap-4 rounded-lg border bg-gray-100 px-3 py-4',
                !hasFreeWithdraw && 'border-none bg-primary/10',
              )}
            >
              <p className="flex items-end gap-2">
                <Money2 className="text-primary" />
                Saque mínimo de{' '}
                {formatMoneyValue(data?.config.minWithdrawAmount || 0)}
              </p>

              <p className="flex items-end gap-2">
                <AlertTriangle className="text-primary" />
                Taxa de {formatMoneyValue(data?.config.withdrawFee || 0)} por
                transação
              </p>

              {hasFreeWithdraw && (
                <p className="flex items-end gap-2">
                  <Clock className="text-primary" />
                  Você tem {data?.config.withdrawsRemaining} saque gratuito por
                  dia
                </p>
              )}
            </div>
            <div className="flex justify-end gap-5">
              <Button
                type="button"
                variant="secondary"
                className="rounded-full px-10 py-5"
                onClick={() => setOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                className="rounded-full px-10 py-5  font-bold text-white"
                type="submit"
                disabled={isSubmitting || isLoading || isLoadingAdmin}
              >
                {isSubmitting || isLoading ? (
                  <Spinner size={'small'} />
                ) : (
                  'Sacar'
                )}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
